/**
 * DevToolbar Component
 *
 * Development-only toolbar for testing and debugging the device updater wizard.
 * Allows skipping to any step without a physical device connected.
 *
 * Features:
 * - Skip to any wizard step
 * - View current wizard state
 * - Inject mock device info
 *
 * SECURITY: This component includes multiple safeguards to prevent rendering
 * in production environments:
 * 1. Checks the `enabled` prop (should only be true in dev)
 * 2. Checks build-time ENVIRONMENT variable
 * 3. Checks runtime hostname for known dev environments
 *
 * All three conditions must pass for the toolbar to render.
 */

import * as React from 'react';
import styled from 'styled-components';
import { useWizard } from '../hooks/useWizard';
import { WizardStep, WIZARD_STEPS_ORDER } from '../types/wizard-types';

// Declare ENVIRONMENT variable (set by webpack)
declare const ENVIRONMENT: string | undefined;

/**
 * Check if we're in a development environment
 * This is a safety check to prevent the toolbar from rendering in production
 * even if the `enabled` prop is somehow set to true.
 */
const isDevEnvironment = (): boolean => {
  // Check build-time environment - if explicitly set to production, block
  if (typeof ENVIRONMENT !== 'undefined' && ENVIRONMENT === 'production') {
    return false;
  }

  // Check runtime hostname for known dev environments
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const isDevHost =
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname.includes('dev.') ||
      hostname.includes('.local');

    // If we're on a production-like hostname, block
    if (!isDevHost && hostname.includes('hawkindynamics.com')) {
      return false;
    }

    return isDevHost;
  }

  return false;
};

/**
 * Props for DevToolbar
 */
export interface DevToolbarProps {
  /** Whether the toolbar should be visible (only enable in dev) */
  enabled?: boolean;
}

/**
 * Step display names
 */
const STEP_DISPLAY_NAMES: Record<WizardStep, string> = {
  [WizardStep.EID_ENTRY]: 'EID Entry',
  [WizardStep.DEVICE_CONNECTION]: 'Device Connection',
  [WizardStep.BOOTLOADER_TRANSFER]: 'Bootloader Transfer',
  [WizardStep.EASY_INSTALLER_LOADING]: 'Easy Installer',
  [WizardStep.FIRMWARE_FLASHING]: 'Firmware Flashing',
  [WizardStep.FINALIZATION]: 'Finalization',
};

/**
 * DevToolbar component
 */
export const DevToolbar: React.FC<DevToolbarProps> = ({ enabled = false }) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const { state, skipToStep, reset } = useWizard();

  // SECURITY: Multiple safeguards to prevent rendering in production
  // 1. Check the enabled prop (controlled by parent component)
  // 2. Check build-time and runtime environment
  if (!enabled || !isDevEnvironment()) {
    return null;
  }

  /**
   * Handle skipping to a step
   */
  const handleSkipToStep = (targetStep: WizardStep) => {
    const targetIndex = WIZARD_STEPS_ORDER.indexOf(targetStep);

    // Mark all previous steps as completed
    const completedSteps = WIZARD_STEPS_ORDER.slice(0, targetIndex);

    skipToStep(targetStep, completedSteps);
  };

  /**
   * Handle start over
   */
  const handleStartOver = () => {
    reset();
  };

  /**
   * Get step index (1-based for display)
   */
  const getStepIndex = (step: WizardStep): number => {
    return WIZARD_STEPS_ORDER.indexOf(step) + 1;
  };

  return (
    <ToolbarContainer data-testid="dev-toolbar">
      <ToggleButton
        onClick={() => setIsExpanded(!isExpanded)}
        $expanded={isExpanded}
      >
        DEV
      </ToggleButton>

      {isExpanded && (
        <ToolbarPanel>
          <Section>
            <SectionTitle>Skip to Step</SectionTitle>
            <StepButtons>
              {WIZARD_STEPS_ORDER.map((step) => (
                <StepButton
                  key={step}
                  onClick={() => handleSkipToStep(step)}
                  $active={state.currentStep === step}
                  data-testid={`dev-skip-${step}`}
                >
                  <StepNumber>{getStepIndex(step)}</StepNumber>
                  {STEP_DISPLAY_NAMES[step]}
                </StepButton>
              ))}
            </StepButtons>
          </Section>

          <Section>
            <SectionTitle>Actions</SectionTitle>
            <ActionButton
              onClick={handleStartOver}
              data-testid='dev-start-over'
            >
              Start Over
            </ActionButton>
          </Section>

          <Section>
            <SectionTitle>Current State</SectionTitle>
            <StateInfo>
              <StateRow>Current: {state.currentStep}</StateRow>
              {state.eid && <StateRow>EID: {state.eid}</StateRow>}
              {state.deviceInfo && (
                <StateRow>
                  Device: {state.deviceInfo.productName}
                </StateRow>
              )}
              {state.error && (
                <StateRow $error>Error: {state.error.code}</StateRow>
              )}
            </StateInfo>
          </Section>
        </ToolbarPanel>
      )}
    </ToolbarContainer>
  );
};

// Styled Components

const ToolbarContainer = styled.div`
  position: fixed;
  bottom: 80px;
  right: 20px;
  z-index: 10000;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, monospace;
`;

const ToggleButton = styled.button<{ $expanded: boolean }>`
  padding: 8px 16px;
  background: ${(props) => (props.$expanded ? '#e91e63' : '#ff5722')};
  color: white;
  border: none;
  border-radius: ${(props) => (props.$expanded ? '8px 8px 0 0' : '8px')};
  cursor: pointer;
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 1px;
  transition: all 0.2s ease;

  &:hover {
    background: #e91e63;
  }
`;

const ToolbarPanel = styled.div`
  background: #1e1e1e;
  border: 1px solid #333;
  border-radius: 8px 0 8px 8px;
  padding: 16px;
  min-width: 280px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
`;

const Section = styled.div`
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.div`
  font-size: 11px;
  font-weight: bold;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 8px;
`;

const StepButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StepButton = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: ${(props) => (props.$active ? '#2196f3' : '#2a2a2a')};
  color: ${(props) => (props.$active ? 'white' : '#ccc')};
  border: 1px solid ${(props) => (props.$active ? '#2196f3' : '#444')};
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  text-align: left;
  transition: all 0.15s ease;

  &:hover {
    background: ${(props) => (props.$active ? '#1976d2' : '#333')};
    border-color: ${(props) => (props.$active ? '#1976d2' : '#555')};
  }
`;

const StepNumber = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  font-size: 10px;
  font-weight: bold;
`;

const ActionButton = styled.button`
  width: 100%;
  padding: 8px 12px;
  background: #4caf50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  transition: background 0.15s ease;

  &:hover {
    background: #43a047;
  }
`;

const StateInfo = styled.div`
  background: #2a2a2a;
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 11px;
  color: #aaa;
`;

const StateRow = styled.div<{ $error?: boolean }>`
  color: ${(props) => (props.$error ? '#ff5252' : '#aaa')};
  margin-bottom: 4px;

  &:last-child {
    margin-bottom: 0;
  }
`;

export default DevToolbar;
